import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination } from "@/components/list-pagination";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { CONTACTS_PER_PAGE } from "@/features/contact/api";
import { ContactTable } from "@/features/contact/contact-table";
import { CreateContactDialog } from "@/features/contact/create-contact/dialog";
import { contactListQuery } from "@/features/contact/queries";
import { StandingNotice } from "@/features/reseller/standing-notice";
import { m } from "@/lib/paraglide/messages.js";

// ?page= is route state (guide, rule 16): a link to page 2 opens page 2. Anything else reads as 1.
const ContactsSearch = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /contacts: the contact book, a page at a time from the API. Only an active reseller adds
// contacts; the button is a courtesy, the API's guard() is the gate (guide, rule 14).
export const Route = createFileRoute("/_authenticated/contacts/")({
  validateSearch: ContactsSearch,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(contactListQuery(deps.page)),
  component: ContactsPage,
});

function ContactsPage() {
  const { reseller } = Route.useRouteContext();
  const { page: asked = 1 } = Route.useSearch();
  const { data } = useSuspenseQuery(contactListQuery(asked));
  const { page, total } = data.meta;
  const pages = Math.max(1, Math.ceil(total / CONTACTS_PER_PAGE));
  const from = data.data.length === 0 ? 0 : (page - 1) * CONTACTS_PER_PAGE + 1;
  const range = m.common_page_range({ from, to: from + data.data.length - (data.data.length ? 1 : 0), total });
  const active = reseller.status === "active";
  return (
    <>
      {!active && <StandingNotice status={reseller.status} />}
      {/* The page's heading: shadcn's Item as shipped, its action a new contact. */}
      <Item>
        <ItemContent>
          <ItemTitle>{m.contacts_nav()}</ItemTitle>
          <ItemDescription>{m.contacts_page_description()}</ItemDescription>
        </ItemContent>
        {active && (
          <ItemActions>
            <CreateContactDialog />
          </ItemActions>
        )}
      </Item>
      <ContactTable
        contacts={data.data}
        canManage={active}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
    </>
  );
}
